import { createSlice } from '@reduxjs/toolkit';
import {
  getOffersBannerThunk,
  getOffersCategoriesThunk,
  getOffersHomeThunk,
  getOffersListThunk,
  getOffersSearchThunk,
  getOffersOneThunk,
  saveOfferThunk,
  unSaveOfferThunk,
} from '../thunk/offers.thunk';

const initialState = {
  home: {
    result: null,
    isLoading: false,
    error: null,
  },
  list: {
    result: null,
    isLoading: false,
    isLoadingMore: false,
    error: null,
    currentIndex: 0,
    hasMore: true,
  },
  record: {
    result: null,
    isLoading: false,
    error: null,
  },
  banners: {
    result: null,
    isLoading: false,
    error: null,
  },
  categories: {
    result: null,
    isLoading: false,
    error: null,
  },
  search: {
    result: null,
    isLoading: false,
    error: null,
  },
  saveFavorites: {
    isLoading: false,
    error: null,
  },
  removeFavorites: {
    isLoading: false,
    error: null,
  },
};

export const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    resetOffersList: (state) => {
      state.list = {
        result: null,
        isLoading: false,
        isLoadingMore: false,
        error: null,
        currentIndex: 0,
        hasMore: true,
      };
    },
    resetOffersSearch: (state) => {
      state.search = {
        result: null,
        isLoading: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOffersHomeThunk.pending, (state) => {
        state.home = {
          isLoading: true,
          result: null,
          error: null,
        };
      })
      .addCase(getOffersHomeThunk.fulfilled, (state, { payload }) => {
        state.home = {
          ...state.home,
          isLoading: false,
          result: payload,
        };
      })
      .addCase(getOffersHomeThunk.rejected, (state, { payload }) => {
        state.home = {
          ...state.home,
          isLoading: false,
          error: payload,
        };
      });

    builder
      .addCase(getOffersListThunk.pending, (state, { meta }) => {
        const isLoadingMore = meta.arg?.index > 0;
        if (isLoadingMore) {
          state.list.isLoadingMore = true;
        } else {
          state.list = {
            ...state.list,
            isLoading: true,
            result: null,
            error: null,
            currentIndex: 0,
            hasMore: true,
          };
        }
      })
      .addCase(getOffersListThunk.fulfilled, (state, { payload, meta }) => {
        const isLoadingMore = meta.arg?.index > 0;
        const newResults = payload?.results || payload?.range || [];
        const limit = meta.arg?.limit || 2;

        if (isLoadingMore) {
          // Accumulate results
          const existingResults = state.list.result?.results || state.list.result?.range || [];
          if (payload?.results) {
            state.list.result = {
              ...payload,
              results: [...existingResults, ...newResults],
            };
          } else if (payload?.range) {
            state.list.result = {
              ...payload,
              range: [...existingResults, ...newResults],
            };
          }
        } else {
          state.list.result = payload;
        }

        state.list.isLoading = false;
        state.list.isLoadingMore = false;
        state.list.currentIndex = meta.arg?.index || 0;
        state.list.hasMore = newResults.length >= limit;
      })
      .addCase(getOffersListThunk.rejected, (state, { payload }) => {
        state.list = {
          ...state.list,
          isLoading: false,
          isLoadingMore: false,
          error: payload,
        };
      });

    builder
      .addCase(getOffersSearchThunk.pending, (state) => {
        state.search = {
          ...state.search,
          isLoading: true,
          error: null,
        };
      })
      .addCase(getOffersSearchThunk.fulfilled, (state, { payload }) => {
        state.search = {
          ...state.search,
          isLoading: false,
          result: payload,
        };
      })
      .addCase(getOffersSearchThunk.rejected, (state, { payload }) => {
        state.search = {
          ...state.search,
          isLoading: false,
          error: payload,
        };
      });

    builder
      .addCase(getOffersOneThunk.pending, (state) => {
        state.record = {
          isLoading: true,
          result: null,
          error: null,
        };
      })
      .addCase(getOffersOneThunk.fulfilled, (state, { payload }) => {
        state.record = {
          ...state.record,
          isLoading: false,
          result: payload,
        };
      })
      .addCase(getOffersOneThunk.rejected, (state, { payload }) => {
        state.record = {
          ...state.record,
          isLoading: false,
          error: payload,
        };
      });

    builder
      .addCase(getOffersCategoriesThunk.pending, (state) => {
        state.categories = {
          isLoading: true,
          result: null,
          error: null,
        };
      })
      .addCase(getOffersCategoriesThunk.fulfilled, (state, { payload }) => {
        state.categories = {
          ...state.categories,
          isLoading: false,
          result: payload,
        };
      })
      .addCase(getOffersCategoriesThunk.rejected, (state, { payload }) => {
        state.categories = {
          ...state.categories,
          isLoading: false,
          error: payload,
        };
      });

    builder
      .addCase(saveOfferThunk.pending, (state) => {
        state.saveFavorites = {
          isLoading: true,
          error: null,
        };
      })
      .addCase(saveOfferThunk.fulfilled, (state) => {
        state.saveFavorites = {
          isLoading: false,
          error: null,
        };
      })
      .addCase(saveOfferThunk.rejected, (state, { payload }) => {
        state.saveFavorites = {
          isLoading: false,
          error: payload,
        };
      });

    builder
      .addCase(unSaveOfferThunk.pending, (state) => {
        state.removeFavorites = {
          isLoading: true,
          error: null,
        };
      })
      .addCase(unSaveOfferThunk.fulfilled, (state) => {
        state.removeFavorites = {
          isLoading: false,
          error: null,
        };
      })
      .addCase(unSaveOfferThunk.rejected, (state, { payload }) => {
        state.removeFavorites = {
          isLoading: false,
          error: payload,
        };
      });

    builder
      .addCase(getOffersBannerThunk.pending, (state) => {
        state.banners = {
          isLoading: true,
          result: null,
          error: null,
        };
      })
      .addCase(getOffersBannerThunk.fulfilled, (state, { payload }) => {
        state.banners = {
          ...state.banners,
          isLoading: false,
          result: payload,
        };
      })
      .addCase(getOffersBannerThunk.rejected, (state, { payload }) => {
        state.banners = {
          ...state.banners,
          isLoading: false,
          error: payload,
        };
      });
  },
});

export const { resetOffersList, resetOffersSearch } = offersSlice.actions;
